import {
  Box,
  useMediaQuery,
  useTheme,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Chip,
  Paper,
  TableSortLabel,
  TablePagination,
  Checkbox,
  Stack,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  PersonOutline as PersonIcon,
  Storefront as StorefrontIcon,
  DevicesOther as DevicesIcon,
} from '@mui/icons-material';
import { useEffect, useState, useMemo, useCallback } from 'react';
import OrderCardItem from './OrderCardItem';
import { Application } from '../types/application';
import STATUS_COLORS from '../../../constants/statusColors';
import { getInflowBucket, getInflowLabel, getInflowColor } from '../../../constants/inflowChannels';

type Props = {
  applications: Application[];
  selectedIds: string[];
  onSelectIdsChange: (ids: string[]) => void;
  onSelect: (app: Application) => void;
};

type Order = keyof Application;

// 📅 날짜 포맷팅 유틸 함수
const formatDateTime = (timestamp: { seconds: number } | undefined): string => {
  if (!timestamp?.seconds) return '-';
  
  try {
    return new Date(timestamp.seconds * 1000).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch (error) {
    console.warn('날짜 포맷팅 오류:', error);
    return '-';
  }
};

// 💰 가격 포맷팅 유틸 함수
const formatPrice = (price: number | undefined): string => {
  if (!price || typeof price !== 'number') return '-';
  return `${price.toLocaleString()}원`;
};

// 📱 기기 정보 포맷팅 유틸 함수 (구성 정보 지원)
const formatDevices = (devices: Application['devices'], type: 'type' | 'model'): string => {
  if (!devices || !Array.isArray(devices) || devices.length === 0) return '-';
  
  if (type === 'type') {
    return Array.from(new Set(devices.map(device => device.deviceType)))
      .filter(Boolean)
      .join(', ') || '-';
  }
  
  // type === 'model'인 경우 구성 정보 처리
  return devices.map(device => {
    const parts = device.deviceModel.split(' - ');
    
    if (parts.length === 2) {
      const modelName = parts[0];
      const secondPart = parts[1];
      
      // 구성 정보인지 확인
      const configKeywords = ['풀세트', '유닛', '본체', '기타'];
      const isConfig = configKeywords.some(keyword => secondPart.includes(keyword));
      
      if (isConfig) {
        return `${modelName} (${secondPart})`;
      }
    }
    
    // 기존 방식 또는 단순한 경우
    return device.deviceModel;
  }).join(', ') || '-';
};

// 🏠 주소 포맷팅 유틸 함수
const formatAddress = (app: Application): string => {
  const parts = [app.pickup_address, app.pickup_address_detail].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '-';
};

/** 유입 구분(채널/소스) 표시 정보를 반환 */
const getChannelInfo = (app: Application): { label: string; color: string; bgColor: string; icon: 'person' | 'store' | 'edit' | 'business' } => {
  if (app.source_type === 'manual') {
    return { label: '수동', color: '#6366f1', bgColor: 'rgba(99, 102, 241, 0.1)', icon: 'edit' };
  }
  if (app.source_type === 'partner') {
    return { label: '거래처', color: '#7c3aed', bgColor: 'rgba(124, 58, 237, 0.1)', icon: 'business' };
  }
  if (app.channel === 'b2c') {
    return { label: '개인', color: '#0ea5e9', bgColor: 'rgba(14, 165, 233, 0.1)', icon: 'person' };
  }
  // 기본: B2B 업체 (channel 미지정 또는 'b2b')
  return { label: '업체', color: '#059669', bgColor: 'rgba(5, 150, 105, 0.1)', icon: 'store' };
};

const channelIconMap = {
  person: PersonIcon,
  store: StorefrontIcon,
  edit: EditIcon,
  business: BusinessIcon,
};

// 테이블 헤더 컬럼 정의
const headCells: { id: string; label: string; sortable: boolean; minWidth?: number }[] = [
  { id: 'application_number', label: '신청번호', sortable: true, minWidth: 100 },
  { id: 'channel_info', label: '구분', sortable: false, minWidth: 72 },
  { id: 'inflow_source', label: '유입경로', sortable: false, minWidth: 90 },
  { id: 'store_code', label: '매장코드', sortable: true, minWidth: 90 },
  { id: 'user_name', label: '이름', sortable: true, minWidth: 70 },
  { id: 'phone_number', label: '전화번호', sortable: true, minWidth: 120 },
  { id: 'full_address', label: '주소', sortable: false, minWidth: 180 },
  { id: 'device_count', label: '기기수', sortable: false, minWidth: 60 },
  { id: 'devices', label: '기기 종류', sortable: false, minWidth: 110 },
  { id: 'devices_detail', label: '기기 상세', sortable: false, minWidth: 140 },
  { id: 'created_at', label: '신청일시', sortable: true, minWidth: 130 },
  { id: 'price_offer', label: '검수금액', sortable: true, minWidth: 90 },
  { id: 'final_price', label: '최종금액', sortable: true, minWidth: 90 },
  { id: 'status', label: '상태', sortable: true, minWidth: 80 },
];

const OrdersTable = ({ applications, selectedIds, onSelectIdsChange, onSelect }: Props) => {
  const [orderBy, setOrderBy] = useState<Order>('created_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // 🔍 디버그 로그 (개발 환경에서만)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📦 OrdersTable - 전달받은 신청서 목록:', applications.length);
    }
  }, [applications]);

  // 📊 정렬된 데이터 메모이제이션
  const sortedApps = useMemo(() => {
    return [...applications].sort((a, b) => {
      const valA = a[orderBy];
      const valB = b[orderBy];
      
      // null/undefined 처리
      if (valA === undefined || valA === null) return 1;
      if (valB === undefined || valB === null) return -1;
      
      // 날짜 객체 처리
      if (orderBy === 'created_at' && typeof valA === 'object' && typeof valB === 'object') {
        const timeA = (valA as any)?.seconds || 0;
        const timeB = (valB as any)?.seconds || 0;
        return order === 'asc' ? timeA - timeB : timeB - timeA;
      }
      
      // 일반 값 비교
      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [applications, orderBy, order]);

  // 📄 페이지네이션된 데이터
  const visibleRows = useMemo(() => {
    return sortedApps.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedApps, page, rowsPerPage]);

  // 🎯 이벤트 핸들러들
  const handleSort = useCallback((property: Order) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  }, [orderBy, order]);

  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const isSelected = useCallback((id: string) => selectedIds.includes(id), [selectedIds]);

  const handleClick = useCallback((id: string) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedIds, id];
    } else {
      newSelected = selectedIds.filter((sid) => sid !== id);
    }

    onSelectIdsChange(newSelected);
  }, [selectedIds, onSelectIdsChange]);

  const handleSelectAllClick = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = visibleRows.map((app) => app.id);
      onSelectIdsChange(allIds);
    } else {
      onSelectIdsChange([]);
    }
  }, [visibleRows, onSelectIdsChange]);

  // 📱 모바일 뷰 렌더링
  if (isMobile) {
    return (
      <Box>
        {visibleRows.map((app) => (
          <OrderCardItem 
            key={app.id} 
            app={app} 
            onSelect={() => onSelect(app)} 
            selected={isSelected(app.id)} 
          />
        ))}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={applications.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ mt: 2 }}
        />
      </Box>
    );
  }

  // 🖥️ 데스크톱 테이블 뷰 렌더링
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        border: '1.5px solid #f0f0f0',
        background: '#fff',
        boxShadow: '0 1.5px 6px 0 #e7ebf0',
      }}
    >
      <Table size="small" aria-label="신청서 목록 테이블">
        <TableHead>
          <TableRow sx={{ height: 64, bgcolor: '#f9fafc' }}>
            {/* 체크박스 헤더 */}
            <TableCell padding="checkbox" sx={{ py: 2 }}>
              <Checkbox
                color="primary"
                indeterminate={selectedIds.length > 0 && selectedIds.length < visibleRows.length}
                checked={visibleRows.length > 0 && selectedIds.length === visibleRows.length}
                onChange={handleSelectAllClick}
                inputProps={{
                  'aria-label': '모든 항목 선택',
                }}
              />
            </TableCell>
            
            {/* 컬럼 헤더들 */}
            {headCells.map((headCell) => (
              <TableCell
                key={headCell.id}
                sortDirection={orderBy === headCell.id ? order : false}
                sx={{
                  fontWeight: 800,
                  fontSize: 15,
                  letterSpacing: '-0.5px',
                  color: '#222',
                  borderBottom: '1.5px solid #e7ebf0',
                  py: 2.2,
                  bgcolor: '#f9fafc',
                  minWidth: headCell.minWidth,
                }}
              >
                {headCell.sortable ? (
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => handleSort(headCell.id as Order)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                ) : (
                  headCell.label
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        
        <TableBody>
          {visibleRows.map((app) => {
            const isItemSelected = isSelected(app.id);
            return (
              <TableRow
                key={app.id}
                hover
                onClick={() => onSelect(app)}
                role="checkbox"
                aria-checked={isItemSelected}
                selected={isItemSelected}
                sx={{
                  cursor: 'pointer',
                  backgroundColor: isItemSelected ? '#e0f2fe' : undefined,
                  transition: 'background 0.2s ease',
                  '&:hover': { 
                    background: '#f3faff',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                  },
                  height: 64,
                }}
              >
                {/* 체크박스 */}
                <TableCell padding="checkbox" sx={{ py: 1.8 }}>
                  <Checkbox
                    color="primary"
                    checked={isItemSelected}
                    onChange={() => handleClick(app.id)}
                    onClick={(e) => e.stopPropagation()}
                    inputProps={{
                      'aria-labelledby': `row-${app.id}`,
                    }}
                  />
                </TableCell>

                {/* 각 컬럼 데이터 */}
                {headCells.map((headCell) => {
                  // 유입 구분 (개인/업체/수동/거래처)
                  if (headCell.id === 'channel_info') {
                    const info = getChannelInfo(app);
                    const IconComp = channelIconMap[info.icon];
                    return (
                      <TableCell key={headCell.id} sx={{ py: 1.7 }}>
                        <Chip
                          icon={<IconComp sx={{ fontSize: 14 }} />}
                          label={info.label}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: 12,
                            color: info.color,
                            bgcolor: info.bgColor,
                            border: `1px solid ${info.color}30`,
                            height: 24,
                            '& .MuiChip-icon': { color: info.color, ml: 0.5 },
                          }}
                        />
                      </TableCell>
                    );
                  }

                  // 📈 마케팅 유입경로 (지마켓/옥션/네이버/쿠팡 등)
                  if (headCell.id === 'inflow_source') {
                    const bucket = getInflowBucket(app);
                    const color = getInflowColor(bucket);
                    const tooltip = app.inflow
                      ? [
                          app.inflow.medium && `매체: ${app.inflow.medium}`,
                          app.inflow.campaign && `캠페인: ${app.inflow.campaign}`,
                          app.inflow.referrer && `referrer: ${app.inflow.referrer}`,
                        ].filter(Boolean).join(' / ')
                      : '';
                    return (
                      <TableCell key={headCell.id} sx={{ py: 1.7 }}>
                        <Tooltip title={tooltip} arrow disableHoverListener={!tooltip}>
                          <Chip
                            label={getInflowLabel(bucket)}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              fontSize: 12,
                              color,
                              bgcolor: `${color}14`,
                              border: `1px solid ${color}30`,
                              height: 24,
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                    );
                  }

                  // 기기 수
                  if (headCell.id === 'device_count') {
                    const count = app.devices?.length ?? 0;
                    return (
                      <TableCell key={headCell.id} sx={{ py: 1.7, textAlign: 'center' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.25,
                            borderRadius: 1.5,
                            bgcolor: count > 1 ? 'rgba(59, 130, 246, 0.08)' : 'rgba(0,0,0,0.04)',
                            border: count > 1 ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid rgba(0,0,0,0.06)',
                          }}
                        >
                          <DevicesIcon sx={{ fontSize: 14, color: count > 1 ? '#3b82f6' : '#9ca3af' }} />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              fontSize: 13,
                              color: count > 1 ? '#3b82f6' : '#6b7280',
                            }}
                          >
                            {count}
                          </Typography>
                        </Box>
                      </TableCell>
                    );
                  }

                  // 주소
                  if (headCell.id === 'full_address') {
                    return (
                      <TableCell key={headCell.id} sx={{ py: 1.7, maxWidth: 200 }}>
                        <Typography variant="body2" noWrap title={formatAddress(app)}>
                          {formatAddress(app)}
                        </Typography>
                      </TableCell>
                    );
                  }
                  
                  // 📱 기기 종류
                  if (headCell.id === 'devices') {
                    return (
                      <TableCell key={headCell.id} sx={{ py: 1.7 }}>
                        <Typography variant="body2" noWrap title={formatDevices(app.devices, 'type')}>
                          {formatDevices(app.devices, 'type')}
                        </Typography>
                      </TableCell>
                    );
                  }
                  
                  // 📱 기기 상세
                  if (headCell.id === 'devices_detail') {
                    return (
                      <TableCell key={headCell.id} sx={{ py: 1.7, maxWidth: 150 }}>
                        <Typography variant="body2" noWrap title={formatDevices(app.devices, 'model')}>
                          {formatDevices(app.devices, 'model')}
                        </Typography>
                      </TableCell>
                    );
                  }
                  
                  // 📅 신청일시
                  if (headCell.id === 'created_at') {
                    return (
                      <TableCell key={headCell.id} sx={{ py: 1.7, minWidth: 140 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {formatDateTime(app.created_at)}
                        </Typography>
                      </TableCell>
                    );
                  }
                  
                  // 💰 가격 필드들
                  if (headCell.id === 'price_offer' || headCell.id === 'final_price') {
                    const price = app[headCell.id as keyof Application] as number;
                    return (
                      <TableCell key={headCell.id} sx={{ fontWeight: 600, py: 1.7, textAlign: 'right' }}>
                        <Typography variant="body2" color={price > 0 ? 'success.main' : 'text.secondary'}>
                          {formatPrice(price)}
                        </Typography>
                      </TableCell>
                    );
                  }
                  
                  // 상태 (상태 칩 + 정보미완성 경고만 표시, 유입 구분은 별도 컬럼)
                  if (headCell.id === 'status') {
                    return (
                      <TableCell key={headCell.id} sx={{ py: 1.7 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Chip
                            label={app.status}
                            size="small"
                            sx={{
                              fontWeight: 700,
                              color: '#fff',
                              bgcolor: STATUS_COLORS[app.status] || '#757575',
                              minWidth: 64,
                              letterSpacing: '-0.5px',
                              boxShadow: `0 1px 8px ${(STATUS_COLORS[app.status] || '#757575')}20`,
                              border: 'none',
                              fontSize: 13,
                              py: 0.5,
                              height: 26,
                              '&:hover': {
                                transform: 'scale(1.02)',
                                boxShadow: `0 2px 12px ${(STATUS_COLORS[app.status] || '#757575')}30`,
                              },
                              transition: 'all 0.2s ease',
                            }}
                          />
                          {app.is_incomplete && (
                            <Tooltip title="정보 미완성 - 주소/계좌 입력 필요" arrow>
                              <Box
                                sx={{
                                  width: 22, height: 22, borderRadius: '50%',
                                  bgcolor: alpha('#f59e0b', 0.15),
                                  border: `1.5px solid ${alpha('#f59e0b', 0.5)}`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  cursor: 'pointer', transition: 'all 0.2s',
                                  '&:hover': { bgcolor: alpha('#f59e0b', 0.25), transform: 'scale(1.1)' },
                                }}
                              >
                                <WarningIcon sx={{ fontSize: 13, color: '#d97706' }} />
                              </Box>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    );
                  }
                  
                  // 🔤 기본 텍스트 필드들
                  return (
                    <TableCell key={headCell.id} sx={{ py: 1.7 }}>
                      <Typography variant="body2">
                        {(app[headCell.id as keyof Application] as string) || '-'}
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
          
          {/* 빈 행 처리 */}
          {visibleRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={headCells.length + 1} sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  표시할 데이터가 없습니다.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      
      {/* 페이지네이션 */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={applications.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="페이지당 행 수:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} / 총 ${count !== -1 ? count : `${to}개 이상`}`
        }
        sx={{
          borderTop: '1px solid #f0f0f0',
          bgcolor: '#fafbfc',
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            fontSize: '0.875rem',
            fontWeight: 500,
          },
        }}
      />
    </Paper>
  );
};

export default OrdersTable;
